import { eventBus, GameEvents } from './events.js';

const STATE_KEY = 'blastgame_state';
const STATE_VERSION = 2;

// ---------------------------------------------------------------------------
// Migrations – keyed by TARGET version number.
// Each function receives the old state and returns a new state object.
// Migrations must be pure (no side-effects) and must preserve all existing
// data that is not being explicitly transformed.
// ---------------------------------------------------------------------------
const migrations = {
  // Migration from v1 (Phase 1) to v2 (Phase 2a)
  2: (state) => {
    return {
      ...state,
      version: 2,
      inventory: state.inventory || {
        coconut: 0, coconut_shell: 0, gem: 0, gold_coin: 0,
        seashell: 0, glass_shard: 0, driftwood: 0, fish: 0,
        melody_fragment: 0, sand_dollar: 0,
      },
      placedObjects: state.placedObjects || [],
      upgrades: state.upgrades || {},
      spawners: state.spawners || { lastSpawnTimes: {}, terrainSeed: null },
      friendship: state.friendship || {
        crab: { points: 0, level: 0 },
        dolphin: { points: 0, level: 0 },
        seagull: { points: 0, level: 0 },
      },
      settings: state.settings || {
        signText: 'Welcome to Palm Island!',
        signStyle: 'driftwood',
        masterVolume: 1.0,
      },
    };
  },
};

// ---------------------------------------------------------------------------
// runMigrations – sequentially applies every migration from the saved
// version up to STATE_VERSION.  Exported so tests can exercise migration
// paths in isolation.
// ---------------------------------------------------------------------------
export function runMigrations(state) {
  let current = { ...state };
  const savedVersion = current.version || 1; // missing version → treat as v1

  for (let v = savedVersion + 1; v <= STATE_VERSION; v++) {
    if (migrations[v]) {
      console.log(`[State] Running migration v${v - 1} → v${v}`);
      current = migrations[v](current);
    }
  }

  current.version = STATE_VERSION;
  return current;
}

const defaultState = {
  version: STATE_VERSION,
  discoveries: {},      // { id: { discovered: true, timestamp, count } }
  stats: {
    totalClicks: 0,
    sessionStart: null,
    firstPlayDate: null,
  },
  chestState: {
    gemsRemaining: 15,
    coinsRemaining: 30,
    lastRefillDate: null,
  },
  inventory: {
    coconut: 0,
    coconut_shell: 0,
    gem: 0,
    gold_coin: 0,
    seashell: 0,
    glass_shard: 0,
    driftwood: 0,
    fish: 0,
    melody_fragment: 0,
    sand_dollar: 0,
  },
  placedObjects: [],  // Array of { id, recipeId, recipeVersion, position: {x,y,z}, rotation: {x,y,z}, timestamp }
  upgrades: {},       // { upgradeId: { purchased: true, timestamp } }
  spawners: {
    lastSpawnTimes: {},   // { resourceType: timestamp }
    terrainSeed: null,    // Generated once on first play
  },
  friendship: {
    crab: { points: 0, level: 0 },
    dolphin: { points: 0, level: 0 },
    seagull: { points: 0, level: 0 },
  },
  settings: {
    signText: 'Welcome to Palm Island!',
    signStyle: 'driftwood',
    masterVolume: 1.0,
  },
};

// ---------------------------------------------------------------------------
// Known resource types — derived from defaultState.inventory keys.
// Used by validateState to strip unknown inventory entries.
// ---------------------------------------------------------------------------
const KNOWN_RESOURCES = Object.keys(defaultState.inventory);

// ---------------------------------------------------------------------------
// Known friendship NPCs — derived from defaultState.friendship keys.
// ---------------------------------------------------------------------------
const KNOWN_NPCS = Object.keys(defaultState.friendship);

// ---------------------------------------------------------------------------
// validateState – defensively fixes any malformed data in a loaded state.
// Never throws.  Logs every fix with a "[State] Validation:" prefix and
// returns the (possibly modified) state object.
// ---------------------------------------------------------------------------
export function validateState(state) {
  if (!state || typeof state !== 'object') {
    console.warn('[State] Validation: state is not an object, returning default');
    return JSON.parse(JSON.stringify(defaultState));
  }

  // --- placedObjects ---
  if (Array.isArray(state.placedObjects)) {
    const requiredFields = ['id', 'recipeId', 'timestamp'];
    const requiredVec3 = ['position', 'rotation'];

    state.placedObjects = state.placedObjects.filter((entry, idx) => {
      if (!entry || typeof entry !== 'object') {
        console.warn(`[State] Validation: placedObjects[${idx}] is not an object — removed`);
        return false;
      }

      // Check scalar required fields
      for (const field of requiredFields) {
        if (entry[field] === undefined || entry[field] === null) {
          console.warn(
            `[State] Validation: placedObjects[${idx}] missing required field "${field}" — removed`
          );
          return false;
        }
      }

      // Check vector-3 fields {x, y, z}
      for (const vec of requiredVec3) {
        const v = entry[vec];
        if (
          !v ||
          typeof v !== 'object' ||
          typeof v.x !== 'number' ||
          typeof v.y !== 'number' ||
          typeof v.z !== 'number'
        ) {
          console.warn(
            `[State] Validation: placedObjects[${idx}] has invalid "${vec}" — removed`
          );
          return false;
        }
      }

      return true;
    });
  }

  // --- inventory ---
  if (state.inventory && typeof state.inventory === 'object') {
    // Remove unknown keys
    for (const key of Object.keys(state.inventory)) {
      if (!KNOWN_RESOURCES.includes(key)) {
        console.warn(`[State] Validation: inventory contains unknown resource "${key}" — removed`);
        delete state.inventory[key];
      }
    }

    // Clamp values to non-negative integers
    for (const key of KNOWN_RESOURCES) {
      if (key in state.inventory) {
        const val = state.inventory[key];
        if (typeof val !== 'number' || !Number.isInteger(val) || val < 0) {
          const clamped = Math.max(0, Math.floor(Number(val) || 0));
          console.warn(
            `[State] Validation: inventory.${key} was ${JSON.stringify(val)}, clamped to ${clamped}`
          );
          state.inventory[key] = clamped;
        }
      }
    }
  }

  // --- friendship ---
  if (state.friendship && typeof state.friendship === 'object') {
    // Remove unknown NPC keys
    for (const key of Object.keys(state.friendship)) {
      if (!KNOWN_NPCS.includes(key)) {
        console.warn(`[State] Validation: friendship contains unknown NPC "${key}" — removed`);
        delete state.friendship[key];
      }
    }

    for (const npc of KNOWN_NPCS) {
      const entry = state.friendship[npc];
      if (!entry || typeof entry !== 'object') continue;

      for (const field of ['points', 'level']) {
        if (field in entry) {
          const val = entry[field];
          if (typeof val !== 'number' || !Number.isInteger(val) || val < 0) {
            const clamped = Math.max(0, Math.floor(Number(val) || 0));
            console.warn(
              `[State] Validation: friendship.${npc}.${field} was ${JSON.stringify(val)}, clamped to ${clamped}`
            );
            entry[field] = clamped;
          }
        }
      }
    }
  }

  // --- settings ---
  if (state.settings && typeof state.settings === 'object') {
    // masterVolume: number in [0, 1]
    if ('masterVolume' in state.settings) {
      let vol = state.settings.masterVolume;
      if (typeof vol !== 'number' || Number.isNaN(vol)) {
        console.warn(
          `[State] Validation: settings.masterVolume was ${JSON.stringify(vol)}, reset to 1`
        );
        state.settings.masterVolume = 1;
      } else if (vol < 0) {
        console.warn(
          `[State] Validation: settings.masterVolume was ${vol}, clamped to 0`
        );
        state.settings.masterVolume = 0;
      } else if (vol > 1) {
        console.warn(
          `[State] Validation: settings.masterVolume was ${vol}, clamped to 1`
        );
        state.settings.masterVolume = 1;
      }
    }

    // signText: string, max 40 chars
    if ('signText' in state.settings) {
      if (typeof state.settings.signText !== 'string') {
        console.warn(
          `[State] Validation: settings.signText was ${JSON.stringify(state.settings.signText)}, reset to default`
        );
        state.settings.signText = defaultState.settings.signText;
      } else if (state.settings.signText.length > 40) {
        console.warn(
          `[State] Validation: settings.signText exceeded 40 chars (${state.settings.signText.length}), truncated`
        );
        state.settings.signText = state.settings.signText.slice(0, 40);
      }
    }
  }

  return state;
}

let currentState = null;
let saveTimeout = null;

/**
 * Returns a deep clone of defaultState.
 * Other modules can use this to inspect the schema shape.
 */
export function createDefaultState() {
  return JSON.parse(JSON.stringify(defaultState));
}

function deepMerge(target, source) {
  const result = { ...target };
  for (const key of Object.keys(source)) {
    if (
      source[key] &&
      typeof source[key] === 'object' &&
      !Array.isArray(source[key]) &&
      target[key] &&
      typeof target[key] === 'object' &&
      !Array.isArray(target[key])
    ) {
      result[key] = deepMerge(target[key], source[key]);
    } else if (!(key in result)) {
      result[key] = source[key];
    }
  }
  return result;
}

export function loadGameState() {
  const raw = localStorage.getItem(STATE_KEY);
  if (!raw) {
    currentState = JSON.parse(JSON.stringify(defaultState));
    currentState.stats.sessionStart = Date.now();
    currentState.stats.firstPlayDate = new Date().toISOString();
    ensureTerrainSeed();
    return currentState;
  }

  try {
    const saved = JSON.parse(raw);
    const savedVersion = saved.version || 1; // missing version → v1

    // Guard: if saved version is from the future, we cannot safely load it.
    if (savedVersion > STATE_VERSION) {
      console.warn(
        `[State] Save version (${savedVersion}) is newer than current STATE_VERSION (${STATE_VERSION}). ` +
        'Falling back to fresh default state.'
      );
      currentState = JSON.parse(JSON.stringify(defaultState));
      currentState.stats.sessionStart = Date.now();
      currentState.stats.firstPlayDate = new Date().toISOString();
      ensureTerrainSeed();
      return currentState;
    }

    // Run migrations if the save is behind the current version
    let migrated = saved;
    if (savedVersion < STATE_VERSION) {
      migrated = runMigrations(saved);
    }

    // deepMerge as a safety net — catches any fields that migrations missed
    currentState = deepMerge(migrated, defaultState);
    currentState.version = STATE_VERSION;

    // Preserve saved discoveries/stats over defaults
    if (migrated.discoveries) currentState.discoveries = migrated.discoveries;
    if (migrated.stats) currentState.stats = { ...defaultState.stats, ...migrated.stats };
    if (migrated.chestState) currentState.chestState = { ...defaultState.chestState, ...migrated.chestState };
    // Validate & fix any malformed data before using the state
    currentState = validateState(currentState);

    currentState.stats.sessionStart = Date.now();
    ensureTerrainSeed();
    return currentState;
  } catch (e) {
    console.error('[State] Save corrupted, starting fresh:', e);
    currentState = JSON.parse(JSON.stringify(defaultState));
    currentState.stats.sessionStart = Date.now();
    currentState.stats.firstPlayDate = new Date().toISOString();
    ensureTerrainSeed();
    return currentState;
  }
}

/**
 * Generate a terrainSeed if one has not been set yet, and persist immediately.
 */
function ensureTerrainSeed() {
  if (currentState.spawners && currentState.spawners.terrainSeed == null) {
    currentState.spawners.terrainSeed = Math.floor(Math.random() * 2147483647);
    // Persist immediately so the seed is never lost
    try {
      localStorage.setItem(STATE_KEY, JSON.stringify(currentState));
    } catch (e) {
      console.error('[State] Failed to persist terrainSeed:', e);
    }
  }
}

export function saveGameState(state) {
  if (state) currentState = state;
  if (!currentState) return;

  if (saveTimeout) clearTimeout(saveTimeout);
  saveTimeout = setTimeout(() => {
    try {
      localStorage.setItem(STATE_KEY, JSON.stringify(currentState));
      eventBus.emit(GameEvents.STATE_SAVED, currentState);
    } catch (e) {
      console.error('[State] Failed to save:', e);
    }
  }, 500);
}

export function getGameState() {
  if (!currentState) return loadGameState();
  return currentState;
}

export function resetGameState() {
  localStorage.removeItem(STATE_KEY);
  currentState = JSON.parse(JSON.stringify(defaultState));
  currentState.stats.sessionStart = Date.now();
  currentState.stats.firstPlayDate = new Date().toISOString();
  ensureTerrainSeed();
  return currentState;
}

// Save on visibilitychange as safety net
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden' && currentState) {
    // Flush immediately (bypass debounce) on page hide
    if (saveTimeout) clearTimeout(saveTimeout);
    try {
      localStorage.setItem(STATE_KEY, JSON.stringify(currentState));
    } catch (e) {
      console.error('[State] Failed to save on visibility change:', e);
    }
  }
});

// Listen for save requests from other systems
eventBus.on(GameEvents.SAVE_REQUESTED, () => {
  saveGameState();
});

// Auto-save on state-mutating events (500ms debounce via saveGameState)
const STATE_MUTATING_EVENTS = [
  GameEvents.RESOURCE_COLLECTED,
  GameEvents.OBJECT_PLACED,
  GameEvents.OBJECT_REMOVED,
  GameEvents.UPGRADE_PURCHASED,
  GameEvents.FRIENDSHIP_CHANGED,
  GameEvents.CRAFT_COMPLETED,
  GameEvents.INVENTORY_CHANGED,
  GameEvents.DISCOVERY_NEW,
];

STATE_MUTATING_EVENTS.forEach(event => {
  eventBus.on(event, () => saveGameState());
});
